import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, X, GitBranch, Star, CheckCircle2, Circle, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  hypothesesFunnelAPI,
  HypothesesFunnelAggregate,
  HypothesesFunnelUser,
  HypothesesFunnelUserDetail,
  FunnelFeedVacancy,
} from '../services/api';

// Публичный сайт — для ссылок на нашу страницу вакансии (/vacancy/:id) из воронки.
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://rabotamax.ru').replace(/\/$/, '');

function formatFeedSalary(s: FunnelFeedVacancy['salary']): string {
  if (!s || (!s.from && !s.to)) return '';
  const cur = s.currency || '₽';
  const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);
  if (s.from && s.to) return `${fmt(s.from)}–${fmt(s.to)} ${cur}`;
  if (s.from) return `от ${fmt(s.from)} ${cur}`;
  if (s.to) return `до ${fmt(s.to)} ${cur}`;
  return '';
}

function formatDt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatSalary(rub: number | null | undefined): string {
  if (!rub || rub <= 0) return '—';
  return new Intl.NumberFormat('ru-RU').format(rub) + ' ₽';
}

const HypothesesFunnelPage: React.FC = () => {
  const [aggregate, setAggregate] = useState<HypothesesFunnelAggregate | null>(null);
  const [users, setUsers] = useState<HypothesesFunnelUser[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loadingAgg, setLoadingAgg] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selected, setSelected] = useState<HypothesesFunnelUserDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Выдача вакансий по гипотезе/цели выбранного юзера.
  const [feed, setFeed] = useState<FunnelFeedVacancy[]>([]);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedHasCriteria, setFeedHasCriteria] = useState(true);
  const [feedMessage, setFeedMessage] = useState<string | undefined>(undefined);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [feedHypothesisId, setFeedHypothesisId] = useState<number>(0);

  const FEED_PAGE = 30;

  const loadFeed = useCallback(async (userId: number, hypothesisId: number, offset = 0, append = false) => {
    try {
      setLoadingFeed(true);
      const data = await hypothesesFunnelAPI.getUserFeed(userId, hypothesisId || undefined, offset, FEED_PAGE);
      if (data.success) {
        setFeed((prev) => (append ? [...prev, ...(data.vacancies || [])] : data.vacancies || []));
        setFeedTotal(data.total || 0);
        setFeedHasCriteria(data.hasCriteria);
        setFeedMessage(data.message);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить выдачу');
      if (!append) {
        setFeed([]);
        setFeedTotal(0);
      }
    } finally {
      setLoadingFeed(false);
    }
  }, []);

  const loadAggregate = useCallback(async () => {
    try {
      setLoadingAgg(true);
      const data = await hypothesesFunnelAPI.aggregate();
      if (data.success) {
        setAggregate(data.aggregate);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить агрегаты');
    } finally {
      setLoadingAgg(false);
    }
  }, []);

  const loadUsers = useCallback(async (page = 1) => {
    try {
      setLoadingUsers(true);
      const data = await hypothesesFunnelAPI.listUsers(page, 50);
      if (data.success) {
        setUsers(data.items || []);
        setPagination(data.pagination);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить пользователей');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadAggregate();
    loadUsers(1);
  }, [loadAggregate, loadUsers]);

  const openUser = useCallback(async (userId: number) => {
    try {
      setLoadingDetail(true);
      setFeed([]);
      setFeedTotal(0);
      setFeedHasCriteria(true);
      setFeedMessage(undefined);
      const data = await hypothesesFunnelAPI.getUserDetail(userId);
      if (data.success) {
        setSelected(data.user);
        // По умолчанию грузим выдачу по primary-гипотезе (как на дашборде юзера).
        const primary = data.user.hypotheses.find((h) => h.isPrimary) || data.user.hypotheses[0];
        const hypId = primary ? primary.id : 0;
        setFeedHypothesisId(hypId);
        void loadFeed(userId, hypId);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить детали');
    } finally {
      setLoadingDetail(false);
    }
  }, [loadFeed]);

  const maxStepCount = aggregate?.steps.reduce((m, s) => Math.max(m, s.count), 0) || 1;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <GitBranch className="h-7 w-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Гипотезы и цели — funnel</h1>
          <p className="text-sm text-gray-600">Прохождение пользователей по шагам онбординга и постановки карьерной цели</p>
        </div>
      </div>

      {/* Funnel chart */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Воронка</h2>
        {loadingAgg ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : aggregate ? (
          <div className="space-y-3">
            {aggregate.steps.map((step, idx) => {
              const widthPct = (step.count / maxStepCount) * 100;
              const dropOffPct =
                idx > 0 && aggregate.steps[idx - 1].count > 0
                  ? Math.round(((aggregate.steps[idx - 1].count - step.count) / aggregate.steps[idx - 1].count) * 100)
                  : 0;
              return (
                <div key={step.key}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">
                      {idx + 1}. {step.label}
                    </span>
                    <span className="text-gray-600">
                      {step.count} {idx > 0 && dropOffPct > 0 && (
                        <span className="text-red-600 ml-2">−{dropOffPct}%</span>
                      )}
                    </span>
                  </div>
                  <div className="bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-blue-500 h-full rounded-full transition-all"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t">
              <div>
                <div className="text-xs text-gray-500 uppercase">Всего пользователей в funnel</div>
                <div className="text-2xl font-bold">{aggregate.usersTotal}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase">Средняя ожидаемая ЗП</div>
                <div className="text-2xl font-bold">{formatSalary(aggregate.avgSalaryTarget)}</div>
              </div>
            </div>
            {aggregate.hypothesesDistribution.length > 0 && (
              <div className="mt-6 pt-4 border-t">
                <h3 className="text-sm font-semibold mb-2">Распределение: количество гипотез на пользователя</h3>
                <div className="flex items-end gap-2 h-24">
                  {aggregate.hypothesesDistribution.map((d) => {
                    const maxUsers = Math.max(...aggregate.hypothesesDistribution.map((x) => x.users));
                    const h = (d.users / maxUsers) * 100;
                    return (
                      <div key={d.hypothesesPerUser} className="flex-1 flex flex-col items-center">
                        <div className="text-xs text-gray-600 mb-1">{d.users}</div>
                        <div
                          className="w-full bg-blue-400 rounded-t"
                          style={{ height: `${h}%` }}
                          title={`${d.users} юзеров с ${d.hypothesesPerUser} гипотезами`}
                        />
                        <div className="text-xs text-gray-700 mt-1">{d.hypothesesPerUser}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-xs text-gray-500 mt-1 text-center">шт. гипотез у пользователя</div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Нет данных</p>
        )}
      </div>

      {/* Users list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h2 className="text-lg font-semibold">Пользователи</h2>
        </div>
        {loadingUsers ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Пользователь</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Резюме</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Гипотез</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Цель</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">ЗП цель</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Воронка</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Активность</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                        Нет данных
                      </td>
                    </tr>
                  )}
                  {users.map((u) => (
                    <tr
                      key={u.userId}
                      className="hover:bg-blue-50 cursor-pointer"
                      onClick={() => openUser(u.userId)}
                    >
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900 truncate max-w-[220px]">{u.fullName || '—'}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[220px]">{u.email || `id:${u.userId}`}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.hasResume ? (
                          <CheckCircle2 className="h-4 w-4 inline text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 inline text-gray-300" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-center font-medium">{u.hypothesesCount}</td>
                      <td className="px-4 py-3 text-center">
                        {u.hasGoal ? (
                          <CheckCircle2 className="h-4 w-4 inline text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 inline text-gray-300" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">{formatSalary(u.salaryTarget)}</td>
                      <td className="px-4 py-3 text-sm text-center font-medium">{u.pipelineCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDt(u.lastActivity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Страница {pagination.page} из {pagination.pages} (всего {pagination.total})
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm border rounded disabled:opacity-50"
                    disabled={pagination.page <= 1}
                    onClick={() => loadUsers(pagination.page - 1)}
                  >
                    Назад
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm border rounded disabled:opacity-50"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => loadUsers(pagination.page + 1)}
                  >
                    Вперёд
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {(selected || loadingDetail) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">
                {loadingDetail ? 'Загрузка…' : selected?.fullName || `Пользователь #${selected?.userId}`}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {loadingDetail && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            )}
            {selected && !loadingDetail && (
              <div className="p-6 space-y-6 text-sm">
                <div>
                  <div className="text-xs text-gray-500 uppercase mb-1">Email</div>
                  <div className="font-medium">{selected.email || '—'}</div>
                </div>

                {selected.salarySubmission && (
                  <section>
                    <h4 className="text-sm font-semibold mb-2">Заполненный опрос ЗП</h4>
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded p-3">
                      <div>
                        <div className="text-xs text-gray-500">Должность</div>
                        <div className="font-medium">{selected.salarySubmission.role || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Город</div>
                        <div className="font-medium">{selected.salarySubmission.city || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Опыт / Уровень</div>
                        <div className="font-medium">
                          {selected.salarySubmission.experience || '—'} / {selected.salarySubmission.level || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Текущая ЗП</div>
                        <div className="font-medium">{formatSalary(selected.salarySubmission.currentSalary)}</div>
                      </div>
                    </div>
                  </section>
                )}

                <section>
                  <h4 className="text-sm font-semibold mb-2">
                    Сгенерированные гипотезы ({selected.hypotheses.length})
                  </h4>
                  {selected.hypotheses.length === 0 ? (
                    <p className="text-gray-500">Нет гипотез</p>
                  ) : (
                    <div className="space-y-2">
                      {selected.hypotheses.map((h) => (
                        <div key={h.id} className="border rounded p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {h.isPrimary && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                              <span className="font-medium">{h.role}</span>
                              {h.tag && <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{h.tag}</span>}
                            </div>
                            <span className="text-xs font-medium text-blue-600">{h.matchPercent}%</span>
                          </div>
                          {h.domain && <div className="text-xs text-gray-600 mt-1">{h.domain}</div>}
                          {h.industries && <div className="text-xs text-gray-500 mt-1">{h.industries}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section>
                  <h4 className="text-sm font-semibold mb-2">Карьерная цель</h4>
                  {selected.goal ? (
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded p-3">
                      <div>
                        <div className="text-xs text-gray-500">Целевая роль</div>
                        <div className="font-medium">{selected.goal.targetRole || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Тип компании</div>
                        <div className="font-medium">{selected.goal.companyType || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Индустрия</div>
                        <div className="font-medium">{selected.goal.industry || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Ожидаемая ЗП</div>
                        <div className="font-medium">{formatSalary(selected.goal.salaryTarget)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Формат / Город</div>
                        <div className="font-medium">
                          {selected.goal.workFormat || '—'} / {selected.goal.city || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Обновлено</div>
                        <div className="font-medium">{formatDt(selected.goal.updatedAt)}</div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">Цель не поставлена</p>
                  )}
                </section>

                <section>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold">
                      Выдача вакансий по гипотезе и цели{feedHasCriteria ? ` (${feedTotal})` : ''}
                    </h4>
                    {selected.hypotheses.length > 0 && (
                      <select
                        className="text-xs border rounded px-2 py-1 max-w-[60%]"
                        value={feedHypothesisId}
                        disabled={loadingFeed}
                        onChange={(e) => {
                          const hypId = Number(e.target.value);
                          setFeedHypothesisId(hypId);
                          void loadFeed(selected.userId, hypId);
                        }}
                      >
                        {selected.hypotheses.map((h) => (
                          <option key={h.id} value={h.id}>
                            {h.isPrimary ? '★ ' : ''}{h.role}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    То, что пользователь видит во вкладке «Подходят» на дашборде (match ≥ 30%, top 30). Ссылки ведут на нашу страницу вакансии.
                  </p>
                  {loadingFeed ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    </div>
                  ) : !feedHasCriteria ? (
                    <p className="text-gray-500">{feedMessage || 'Нет гипотез/цели — выдача не персонализируется'}</p>
                  ) : feed.length === 0 ? (
                    <p className="text-gray-500">Нет подходящих вакансий по этой гипотезе</p>
                  ) : (
                    <div className="border rounded divide-y max-h-80 overflow-y-auto">
                      {feed.map((v) => {
                        const sal = formatFeedSalary(v.salary);
                        return (
                          <div key={v.id} className="px-3 py-2 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <a
                                href={v.vacancyUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm font-medium text-blue-600 hover:underline inline-flex items-center gap-1"
                              >
                                <span className="truncate">{v.title}</span>
                                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                              </a>
                              <div className="text-xs text-gray-500 truncate">
                                {v.company || '—'}
                                {v.location ? ` · ${v.location}` : ''}
                                {sal ? ` · ${sal}` : ''}
                                {v.source ? ` · ${v.source}` : ''}
                              </div>
                            </div>
                            <span className="text-xs font-semibold text-blue-600 whitespace-nowrap">{v.matchPercent}%</span>
                          </div>
                        );
                      })}
                      {feed.length < feedTotal && (
                        <div className="px-3 py-2">
                          <button
                            type="button"
                            disabled={loadingFeed}
                            onClick={() => void loadFeed(selected.userId, feedHypothesisId, feed.length, true)}
                            className="w-full text-sm border rounded py-1.5 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          >
                            {loadingFeed && <Loader2 className="h-4 w-4 animate-spin" />}
                            Загрузить ещё ({feed.length} из {feedTotal})
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </section>

                <section>
                  <h4 className="text-sm font-semibold mb-2">
                    Воронка вакансий ({selected.pipeline.length})
                  </h4>
                  {selected.pipeline.length === 0 ? (
                    <p className="text-gray-500">Нет вакансий в воронке</p>
                  ) : (
                    <div className="border rounded divide-y max-h-64 overflow-y-auto">
                      {selected.pipeline.map((p) => (
                        <div key={`${p.vacancyId}-${p.movedAt}`} className="px-3 py-2 flex items-center justify-between">
                          <div className="text-sm">
                            <a
                              href={`${SITE_URL}/vakansiya/v-${p.vacancyId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-blue-600 hover:underline inline-flex items-center gap-1"
                            >
                              vacancy #{p.vacancyId}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                            <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{p.stage}</span>
                          </div>
                          <span className="text-xs text-gray-500">{formatDt(p.movedAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {selected.resumes.length > 0 && (
                  <section>
                    <h4 className="text-sm font-semibold mb-2">Резюме ({selected.resumes.length})</h4>
                    <div className="border rounded divide-y">
                      {selected.resumes.map((r) => (
                        <div key={r.id} className="px-3 py-2 flex items-center justify-between text-sm">
                          <span className="font-medium">{r.title || `Резюме #${r.id}`}</span>
                          <span className="text-xs text-gray-500">{formatDt(r.updatedAt)}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HypothesesFunnelPage;
